"use client";

import React, { useEffect, useMemo, useState } from "react";
import { trackEvent } from "./utils/analytics";
import "./styles/inci-page.css";

// локальные картинки
import kaspiLogo from "./assets/kaspi.png";
import siteLogo from "./assets/logo2.png";

// ====== INCI: ссылки ======
const DG_FIRM_URL = "https://2gis.kz/kostanay/geo/70000001047973162";
const INSTAGRAM_URL = "https://www.instagram.com/inci.kstn?igsh=MWVlOGR2NTltYzZtYg==";
const KASPI_URL = "https://l.kaspi.kz/shop/EsSUvyeVUdM6yak";
const TELEGRAM_URL = "https://t.me/+t9DbafrkYONmZDYy";
const WHATSAPP_URL = "https://wa.me/message/ZQXUYTNLMGP4H1";

// Адрес
const ADDRESS = "Костанай, улица Гоголя, 62";

// Координаты (lat, lon)
const MAP_CENTER: [number, number] = [53.208617, 63.63482];
const MAP_ZOOM = 18;

// Ссылка для кнопок "Открыть в 2GIS"
const OPEN_URL = DG_FIRM_URL;

// ====== Иконки ======
const ICONS = {
  instagram: "https://cdn.simpleicons.org/instagram/E4405F",
  whatsapp: "https://cdn.simpleicons.org/whatsapp/25D366",
  telegram: "https://cdn.simpleicons.org/telegram/26A5E4",
  kaspi: kaspiLogo,
  map: "https://api.iconify.design/mdi:map-marker.svg?color=%230EA5E9",
  arrow: "https://api.iconify.design/mdi:arrow-top-right.svg?color=%236B7280",
};

// тип иконки — ссылка или импортнутый png (у него есть поле src)
type IconLike = string | { src: string };

// Яндекс-карта через iframe
function YandexMap({
  center,
  zoom,
  title = "INCI",
}: {
  center: [number, number];
  zoom: number;
  title?: string;
}) {
  const [lat, lon] = center;

  const src = useMemo(() => {
    const ll = `${lon},${lat}`;
    const z = Math.min(19, Math.max(3, Math.round(zoom)));
    return `https://yandex.ru/map-widget/v1/?ll=${encodeURIComponent(ll)}&z=${z}&pt=${encodeURIComponent(
      `${lon},${lat},pm2rdm`
    )}`;
  }, [lat, lon, zoom]);

  return (
    <div className="ip-map ip-mapYandex" aria-label={`${title} на карте`}>
      <iframe
        title={`${title} — Яндекс Карты`}
        src={src}
        width="100%"
        height="100%"
        frameBorder={0}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

function LinkCard({
  href,
  icon,
  title,
  subtitle,
  onTrack,
}: {
  href: string;
  icon: IconLike;
  title: string;
  subtitle: string;
  onTrack?: () => void;
}) {
  const iconSrc = typeof icon === "string" ? icon : icon.src;

  return (
    <a
      className="ip-action"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        try {
          onTrack?.();
        } catch {}
      }}
    >
      <span className="ip-iconWrap">
        <img className="ip-ico" src={iconSrc} alt="" aria-hidden="true" />
      </span>

      <span className="ip-actionText">
        <span className="ip-actionTitle">{title}</span>
        <span className="ip-actionSub">{subtitle}</span>
      </span>

      <span className="ip-actionRight" aria-hidden="true">
        <img className="ip-icoSm" src={ICONS.arrow} alt="" />
      </span>
    </a>
  );
}

export default function Page() {
  const [toast, setToast] = useState("");

  useEffect(() => {
    trackEvent("visit_info_page", { page: "INCI" });
  }, []);

  const copyToClipboard = async (text: string) => {
    trackEvent("click_copy_text", { text });

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setToast("Скопировано");
      trackEvent("copy_text_success", { text });
    } catch {
      setToast("Не удалось скопировать");
      trackEvent("copy_text_fail", { text });
    } finally {
      window.clearTimeout((copyToClipboard as any)._t);
      (copyToClipboard as any)._t = window.setTimeout(() => setToast(""), 1600);
    }
  };

  return (
    <div className="ip-page">
      <div className="ip-wrap">
        <div className="ip-hero">
          <div className="ip-heroInner">
            {/* ✅ 2 контейнера внутри общего: слева лого, справа 2 кнопки */}
            <div className="ip-heroRow">
              <a
                className="ip-logoLink"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("click_logo", { place: "hero", href: INSTAGRAM_URL })}
                aria-label="INCI — Instagram"
                title="INCI"
              >
                <img className="ip-logo" src={siteLogo.src} alt="INCI" />
              </a>

              <div className="ip-heroRight" aria-label="Быстрые действия">
                <a
                  className="ip-heroBox ip-heroBoxRoute"
                  href={OPEN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Открыть в 2GIS"
                  title={ADDRESS}
                  onClick={() => trackEvent("click_2gis_open_header", { href: OPEN_URL })}
                >
                  <img className="ip-icoSm" src={ICONS.map} alt="" aria-hidden="true" />
                  <span>Как добраться</span>
                </a>

                <a
                  className="ip-heroBox ip-heroBoxWhatsapp"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("click_whatsapp_header", { href: WHATSAPP_URL })}
                  title="Написать в WhatsApp"
                >
                  <img className="ip-icoSm" src={ICONS.whatsapp} alt="" aria-hidden="true" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="ip-grid">
          <div className="ip-card">
            <div className="ip-sectionTitle">Ссылки</div>

            <div className="ip-actions">
              <LinkCard
                href={INSTAGRAM_URL}
                icon={ICONS.instagram}
                title="Instagram"
                subtitle="@inci.kstn"
                onTrack={() => trackEvent("click_instagram", { href: INSTAGRAM_URL })}
              />

              <LinkCard
                href={TELEGRAM_URL}
                icon={ICONS.telegram}
                title="Telegram"
                subtitle="Открыть Telegram"
                onTrack={() => trackEvent("click_telegram", { href: TELEGRAM_URL })}
              />

              <LinkCard
                href={KASPI_URL}
                icon={ICONS.kaspi}
                title="Kaspi Магазин"
                subtitle="Открыть витрину"
                onTrack={() => trackEvent("click_kaspi", { href: KASPI_URL })}
              />
            </div>
          </div>

          <div className="ip-card ip-cardFull">
            <div className="ip-mapTop">
              <div className="ip-address">
                <img className="ip-ico" src={ICONS.map} alt="" aria-hidden="true" />
                {ADDRESS}
              </div>

              <a
                className="ip-miniBtn"
                href={OPEN_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("click_2gis_open", { href: OPEN_URL, place: "map_card" })}
              >
                Открыть в 2GIS
                <img className="ip-icoSm" src={ICONS.arrow} alt="" aria-hidden="true" />
              </a>
            </div>

            <YandexMap center={MAP_CENTER} zoom={MAP_ZOOM} title="INCI" />
          </div>
        </div>
      </div>

      {toast ? (
        <div className="ip-toast" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}
    </div>
  );
}